import { JWMCard } from "@components/JWMCard/JWMCard";
import { JWMLink } from "@components/JWMLink/JWMLink";
import getTranslation from "@components/RecipeGrid/util/getTranslation";
import { useQSSKey } from "@contexts/QueryStringStorageContext";
import { useQueryStringStorage } from "@hooks/useQueryStringStorage/useQueryStringStorage";
import useService, { ServiceTypes } from "@hooks/useService/useService";
import { CardImpression } from "@jmc/core/src/components/CardImpression/index";
import { Tag } from "@jmc/solid-design-system/src/components/atoms/Tag/Tag";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import React from "react";
import { useTranslation } from "react-i18next";

import { RecipeHit as RecipeHitType } from "../types/RecipeHit";
import style from "./style.module.scss";

const RecipeHit = (props: {
    hit: RecipeHitType;
    placement: string;
    onClick?: (hit: RecipeHitType) => void;
    refinements?: { type: string; refine: (string) => string }[];
    hoverOnTags?: boolean;
}): JSX.Element => {
    const { t } = useTranslation();
    const { jnjFullBranded } = useJnjBranding();

    const { hit, placement, onClick, refinements, hoverOnTags = false } = props;
    const { specialty = [], dietary_wishes = [], course = [], difficulty } = hit;
    const tags = [
        ...specialty.map((label) => ({ label, type: "specialty" })),
        ...dietary_wishes.map((label) => ({ label, type: "dietary_wishes" })),
        ...course.map((label) => ({ label, type: "course" })),
        difficulty && { label: difficulty, type: "difficulty" },
    ];

    const qss = useQueryStringStorage();
    const qssKey = useQSSKey();

    const recipeService = useService(ServiceTypes.RECIPE);

    const tagsContent = (
        <div className={style.recipeFooter}>
            <div>
                {tags?.map((tag: { label: string; type: string }, index: number) => {
                    if (tag !== null) {
                        const currentRefinement = refinements && refinements?.find((r) => r.type === tag?.type);
                        return (
                            <Tag
                                key={index}
                                color="default"
                                enableHover={hoverOnTags}
                                dismissable={false}
                                onClick={(e) => {
                                    currentRefinement && currentRefinement?.refine(tag?.label);
                                    qss.handleQueryParam(qssKey, tag?.type, tag?.label);
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                            >
                                <Typography size="xs">{getTranslation(tag?.label, t)}</Typography>
                            </Tag>
                        );
                    }
                })}
            </div>
        </div>
    );

    const urlPrefix = recipeService?.isEnabled ? recipeService?.url : "recipes";
    const button = jnjFullBranded
        ? [
              {
                  link: { url: hit?.uri, url_prefix: urlPrefix, external: false },
                  text: t("Read more", { ns: "common" }),
                  buttonProps: { variant: "naked", color: "primary-550", flexibleWidth: true, justifiedBetween: true },
              },
          ]
        : null;

    return (
        <span onClick={() => onClick && onClick(hit)} role="presentation">
            <JWMLink
                key={hit?.__position}
                url={hit?.uri}
                url_prefix={urlPrefix}
                sendOrigin={true}
                data-recipe-id={hit?.__position}
            >
                <CardImpression
                    type="recipe"
                    name={hit?.title}
                    placement={placement}
                    position={hit?.__position}
                    targeturl={`/recipes${hit?.uri}`}
                >
                    <JWMCard
                        button={button}
                        id={null}
                        description={null}
                        hasOverlay={null}
                        title={hit?.title}
                        image={hit?.imageData}
                        footer={jnjFullBranded ? null : tagsContent}
                        tags={jnjFullBranded ? tagsContent : null}
                    />
                </CardImpression>
            </JWMLink>
        </span>
    );
};

export default RecipeHit;
